/**
 * The icon set.
 *
 * One 24x24 stroke grid, one component, one registry — so icons cannot drift in
 * weight or size between screens. Icons are decorative by default
 * (`aria-hidden`); pass a `title` when an icon is the only label an action has,
 * which turns it into an accessible graphic.
 */

export type IconName =
  | 'overview'
  | 'downline'
  | 'tree'
  | 'exposure'
  | 'net-exposure'
  | 'pnl'
  | 'transfers'
  | 'commission'
  | 'turnover'
  | 'activity'
  | 'settings'
  | 'search'
  | 'filter'
  | 'plus'
  | 'minus'
  | 'give'
  | 'take'
  | 'chevron-down'
  | 'chevron-right'
  | 'chevron-left'
  | 'chevron-up'
  | 'arrow-up'
  | 'arrow-down'
  | 'arrow-right'
  | 'external'
  | 'close'
  | 'check'
  | 'more'
  | 'info'
  | 'warning'
  | 'error'
  | 'lock'
  | 'unlock'
  | 'refresh'
  | 'download'
  | 'calendar'
  | 'clock'
  | 'user'
  | 'users'
  | 'wallet'
  | 'eye'
  | 'eye-off'
  | 'edit'
  | 'copy'
  | 'menu'
  | 'logout'
  | 'sport'
  | 'live'
  | 'shield'
  | 'sliders'
  | 'grip'

/** Path data on a 24x24 grid, stroked. Keep every icon visually 20x20 inside it. */
const PATHS: Record<IconName, string> = {
  overview: 'M4 13h6V4H4v9Zm0 7h6v-5H4v5Zm10 0h6v-9h-6v9Zm0-16v5h6V4h-6Z',
  downline: 'M12 3v4M12 7H6v4M12 7h6v4M6 11v3M18 11v3M12 7v7M4 14h4v4H4v-4Zm6 0h4v4h-4v-4Zm6 0h4v4h-4v-4Z',
  tree: 'M5 5h5v4H5V5Zm9 10h5v4h-5v-4Zm0-10h5v4h-5V5ZM7 9v6h7M7 15v0',
  exposure: 'M3 20h18M6 20V10M11 20V5M16 20v-7M21 20v-4',
  /** Filled fan mark — rendered specially below, not as a stroke path. */
  'net-exposure': '',
  pnl: 'M3 17l5-6 4 3 5-7 4 4M3 21h18',
  transfers: 'M4 8h13l-3-3M20 16H7l3 3',
  commission: 'M12 3v18M8 7h6a3 3 0 0 1 0 6H9a3 3 0 0 0 0 6h7',
  turnover: 'M4 12a8 8 0 0 1 13.7-5.6M20 12a8 8 0 0 1-13.7 5.6M18 3v4h-4M6 21v-4h4',
  activity: 'M3 12h4l3-8 4 16 3-8h4',
  settings:
    'M12 15a3 3 0 1 0 0-6 3 3 0 0 0 0 6Zm8-3a8 8 0 0 0-.2-1.7l2-1.5-2-3.5-2.3 1a8 8 0 0 0-2.9-1.7L14.2 2H9.8l-.4 2.6a8 8 0 0 0-3 1.7l-2.2-1-2 3.5 2 1.5a8 8 0 0 0 0 3.4l-2 1.5 2 3.5 2.3-1a8 8 0 0 0 2.9 1.7l.4 2.6h4.4l.4-2.6a8 8 0 0 0 2.9-1.7l2.3 1 2-3.5-2-1.5c.1-.6.2-1.1.2-1.7Z',
  search: 'M11 18a7 7 0 1 0 0-14 7 7 0 0 0 0 14Zm5.5-1.5L21 21',
  filter: 'M3 5h18l-7 8v6l-4 2v-8L3 5Z',
  plus: 'M12 5v14M5 12h14',
  minus: 'M5 12h14',
  give: 'M12 19V5M12 5l-6 6M12 5l6 6',
  take: 'M12 5v14M12 19l-6-6M12 19l6-6',
  'chevron-down': 'M6 9l6 6 6-6',
  'chevron-right': 'M9 6l6 6-6 6',
  'chevron-left': 'M15 6l-6 6 6 6',
  'chevron-up': 'M6 15l6-6 6 6',
  'arrow-up': 'M12 19V5M6 11l6-6 6 6',
  'arrow-down': 'M12 5v14M6 13l6 6 6-6',
  'arrow-right': 'M5 12h14M13 6l6 6-6 6',
  external: 'M14 4h6v6M20 4l-9 9M18 14v5a1 1 0 0 1-1 1H5a1 1 0 0 1-1-1V7a1 1 0 0 1 1-1h5',
  close: 'M6 6l12 12M18 6L6 18',
  check: 'M4 12l5 5L20 6',
  more: 'M12 6h.01M12 12h.01M12 18h.01',
  info: 'M12 21a9 9 0 1 0 0-18 9 9 0 0 0 0 18Zm0-9v5m0-9h.01',
  warning: 'M12 3 2 20h20L12 3Zm0 6v5m0 3h.01',
  error: 'M12 21a9 9 0 1 0 0-18 9 9 0 0 0 0 18Zm3-12-6 6m0-6 6 6',
  lock: 'M6 11h12v9H6v-9Zm3 0V8a3 3 0 0 1 6 0v3',
  unlock: 'M6 11h12v9H6v-9Zm3 0V8a3 3 0 0 1 5.7-1.3',
  refresh: 'M20 11A8 8 0 0 0 6.3 6.3L4 8.5M4 13a8 8 0 0 0 13.7 4.7L20 15.5M4 4v4.5h4.5M20 20v-4.5h-4.5',
  download: 'M12 3v12M7 11l5 5 5-5M4 20h16',
  calendar: 'M4 6h16v15H4V6Zm0 5h16M9 3v4M15 3v4',
  clock: 'M12 21a9 9 0 1 0 0-18 9 9 0 0 0 0 18Zm0-14v5l3.5 2',
  user: 'M12 12a4 4 0 1 0 0-8 4 4 0 0 0 0 8Zm-8 9a8 8 0 0 1 16 0',
  users: 'M9 12a4 4 0 1 0 0-8 4 4 0 0 0 0 8Zm-7 9a7 7 0 0 1 14 0M17 4.5a4 4 0 0 1 0 7.5M18 21a7 7 0 0 0-3-5.7',
  wallet: 'M3 7h15a2 2 0 0 1 2 2v9a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V7Zm0 0V6a2 2 0 0 1 2-2h11M16 13h2',
  eye: 'M2 12s3.6-6 10-6 10 6 10 6-3.6 6-10 6-10-6-10-6Zm10 2.5a2.5 2.5 0 1 0 0-5 2.5 2.5 0 0 0 0 5Z',
  'eye-off': 'M4 4l16 16M10 6.2A9.6 9.6 0 0 1 12 6c6.4 0 10 6 10 6a17 17 0 0 1-3.3 3.7M6.5 8.3C3.9 9.9 2 12 2 12s3.6 6 10 6c1 0 2-.2 2.8-.4M9.9 9.9a3 3 0 0 0 4.2 4.2',
  edit: 'M4 20h4L20 8l-4-4L4 16v4Zm10-14 4 4',
  copy: 'M9 9h11v11H9V9Zm-4 6H4V4h11v1',
  menu: 'M4 7h16M4 12h16M4 17h16',
  logout: 'M14 4h5a1 1 0 0 1 1 1v14a1 1 0 0 1-1 1h-5M10 8l-4 4 4 4M6 12h11',
  sport: 'M12 21a9 9 0 1 0 0-18 9 9 0 0 0 0 18ZM3.5 9h17M3.5 15h17M12 3a14 14 0 0 1 0 18M12 3a14 14 0 0 0 0 18',
  live: 'M12 14a2 2 0 1 0 0-4 2 2 0 0 0 0 4Zm-4.2 3.2a6 6 0 0 1 0-8.4m8.4 0a6 6 0 0 1 0 8.4M5 20a10 10 0 0 1 0-16m14 0a10 10 0 0 1 0 16',
  shield: 'M12 3l8 3v6c0 5-3.4 8.4-8 9.5C7.4 20.4 4 17 4 12V6l8-3Z',
  sliders: 'M4 8h10M18 8h2M4 16h4M12 16h8M16 5v6M8 13v6',
  grip: 'M9 6h.01M15 6h.01M9 12h.01M15 12h.01M9 18h.01M15 18h.01',
}

export interface IconProps extends Omit<React.SVGProps<SVGSVGElement>, 'name'> {
  name: IconName
  /** Pixel size — 16 in dense rows, 18 default, 20 in nav. */
  size?: number
  /** Supplying a title makes the icon an accessible graphic rather than decoration. */
  title?: string
}

/**
 * Backend-UI Net Exposure mark — fan blades + pin. Filled, animated via CSS
 * (`.w7-icon-net-exposure`). Built for the “Net Exposure” menu title.
 */
function NetExposureMark({
  size,
  title,
  className,
  ...rest
}: Omit<IconProps, 'name'>) {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="currentColor"
      className={['w7-icon-net-exposure', className].filter(Boolean).join(' ')}
      aria-hidden={title ? undefined : true}
      role={title ? 'img' : undefined}
      focusable="false"
      {...rest}
    >
      {title ? <title>{title}</title> : null}
      <g className="w7-ne-fan">
        <path className="w7-ne-blade" d="M1.09 16.68L3.3 12.15L7.18 15.3L5.96 17.81Z" />
        <path className="w7-ne-blade" d="M4.08 11.28L8.35 8.61L9.98 13.34L7.62 14.82Z" />
        <path className="w7-ne-blade" d="M9.48 8.29L14.52 8.29L13.39 13.16L10.61 13.16Z" />
        <path className="w7-ne-blade" d="M15.65 8.61L19.92 11.28L16.38 14.82L14.02 13.34Z" />
        <path className="w7-ne-blade" d="M20.7 12.15L22.91 16.68L18.04 17.81L16.82 15.3Z" />
      </g>
      <path
        className="w7-ne-pin"
        d="M12 7.4c1.6 2.8 2 5.4.2 8.8-.1.3-.2.5-.2.5s-.1-.2-.2-.5C10 12.8 10.4 10.2 12 7.4Z"
      />
    </svg>
  )
}

export function Icon({ name, size = 18, title, ...rest }: IconProps) {
  if (name === 'net-exposure') {
    return <NetExposureMark size={size} title={title} {...rest} />
  }
  const d = PATHS[name]
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth={1.7}
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden={title ? undefined : true}
      role={title ? 'img' : undefined}
      focusable="false"
      {...rest}
    >
      {title ? <title>{title}</title> : null}
      <path d={d} />
    </svg>
  )
}
